// src/components/tuition/TuitionActivation.jsx
import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { FaCheckCircle, FaLock, FaBuilding, FaUserTie, FaEnvelope, FaArrowRight, FaShieldAlt, FaEye, FaEyeSlash } from "react-icons/fa";
import { toast } from "react-toastify";
import { useAppTheme } from "../../context/AppThemeContext";
import api from "../../api/httpClient";
import { API_PATHS } from "../../api/apiPaths";
import { normalizeRole } from "../../utils/authRole";

export default function TuitionActivation() {
  const { isDarkMode } = useAppTheme();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || searchParams.get("activationToken") || "";
  const [pendingInstitute, setPendingInstitute] = useState(null);
  const [verifying, setVerifying] = useState(true);
  const [verificationError, setVerificationError] = useState("");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [activatedSuccess, setActivatedSuccess] = useState(false);

  useEffect(() => {
    let active = true;
    if (!token) {
      setVerificationError("This activation link is missing its security token. Ask the administrator to resend it.");
      setVerifying(false);
      return () => { active = false; };
    }
    api.get(API_PATHS.tuition.verifyActivation(token), { skipAuth: true })
      .then((response) => {
        if (!active) return;
        const payload = response?.data?.data ?? response?.data;
        setPendingInstitute(payload?.institute || payload?.tuition || payload);
      })
      .catch((error) => {
        if (active) setVerificationError(error?.message || "This activation link is invalid or has expired.");
      })
      .finally(() => { if (active) setVerifying(false); });
    return () => { active = false; };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters long.");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }
    if (!agreeTerms) {
      toast.error("Please accept the terms to activate your tuition account.");
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post(API_PATHS.tuition.activate(), { token, password }, { skipAuth: true });
      const payload = response?.data?.data ?? response?.data ?? {};
      const user = payload.user || payload.account || {};
      const role = normalizeRole(payload.role || user.role || user.roles?.[0] || "ROLE_ADMIN");
      localStorage.setItem("user", role);
      localStorage.setItem("currentUserName", user?.name || pendingInstitute?.ownerName || "Tuition Founder");
      setActivatedSuccess(true);
      toast.success("Account successfully activated! Welcome to ClassNX.");
    } catch (error) {
      toast.error(error?.message || "Unable to activate this tuition account.");
    } finally {
      setSubmitting(false);
    }
  };

  if (verifying || verificationError) {
    return <div className={`min-h-screen flex items-center justify-center p-6 ${isDarkMode ? "bg-slate-950 text-slate-100" : "bg-slate-50 text-slate-900"}`}><div className="max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-xl dark:border-slate-800 dark:bg-slate-900"><h1 className="text-xl font-bold">{verifying ? "Checking activation link..." : "Activation link unavailable"}</h1>{verificationError && <p className="mt-3 text-sm text-rose-600">{verificationError}</p>}</div></div>;
  }

  if (activatedSuccess) {
    return (
      <div className={`min-h-screen py-16 px-4 flex items-center justify-center ${isDarkMode ? "bg-slate-950 text-slate-100" : "bg-gradient-to-b from-slate-50 to-emerald-50/50 text-slate-900"}`}>
        <div className={`w-full max-w-md rounded-3xl border p-8 text-center shadow-2xl ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-500/10 text-emerald-500 text-3xl mb-4">
            <FaCheckCircle />
          </div>
          <h2 className="text-2xl font-black">Tuition Workspace Active!</h2>
          <p className="mt-2 text-sm text-slate-500">
            <strong>{pendingInstitute?.name}</strong> has been onboarded and activated on the ClassNX platform.
          </p>

          <button
            type="button"
            onClick={() => window.location.assign("/tuition/dashboard")}
            className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-emerald-600 py-3.5 text-sm font-bold text-white shadow-lg shadow-emerald-600/30 hover:bg-emerald-700 transition"
          >
            Go to Tuition Dashboard <FaArrowRight />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen py-16 px-4 sm:px-6 lg:px-8 flex items-center justify-center ${isDarkMode ? "bg-slate-950 text-slate-100" : "bg-gradient-to-b from-slate-50 to-sky-50/50 text-slate-900"}`}>
      <div className="w-full max-w-md">
        <div className={`rounded-3xl border p-8 sm:p-10 shadow-2xl ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
          <Link
            to="/"
            className="text-xs text-slate-400 hover:text-sky-500 font-bold mb-3 inline-flex items-center gap-1 transition"
          >
            ← Return to ClassNX Home
          </Link>
          <br />
          {/* Badge */}
          <div className="inline-flex items-center gap-2 rounded-full border border-sky-500/30 bg-sky-500/10 px-3.5 py-1 text-xs font-bold uppercase tracking-wider text-sky-500 mb-4">
            <FaShieldAlt /> ClassNX Tuition Onboarding
          </div>

          <h1 className="text-2xl sm:text-3xl font-black tracking-tight">
            Activate Your Tuition Account
          </h1>
          <p className="mt-2 text-xs sm:text-sm text-slate-500">
            You were onboarded by ClassNX Super Admin. Set your secure password to access your Tuition Management Portal.
          </p>

          {/* Onboarded Institute Info Card */}
          <div className={`mt-5 rounded-2xl border p-4 space-y-2 text-xs ${isDarkMode ? "border-slate-800 bg-slate-950/60 text-slate-300" : "border-slate-200 bg-slate-50 text-slate-700"}`}>
            <div className="flex items-center gap-2">
              <FaBuilding className="text-sky-500 shrink-0" />
              <span className="font-semibold text-slate-400">Institute:</span>
              <strong className="text-slate-900 dark:text-slate-100">{pendingInstitute?.name}</strong>
            </div>
            <div className="flex items-center gap-2">
              <FaUserTie className="text-sky-500 shrink-0" />
              <span className="font-semibold text-slate-400">Founder / Owner:</span>
              <span>{pendingInstitute?.ownerName}</span>
            </div>
            <div className="flex items-center gap-2">
              <FaEnvelope className="text-sky-500 shrink-0" />
              <span className="font-semibold text-slate-400">Email:</span>
              <span>{pendingInstitute?.ownerEmail}</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">
                Create Master Password <span className="text-rose-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  required
                  className={`w-full rounded-2xl border px-4 py-3 pr-12 text-sm font-semibold transition focus:border-sky-500 focus:outline-none focus:ring-4 focus:ring-sky-500/10 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300 bg-slate-50 text-slate-900"}`}
                />
                <button type="button" onClick={() => setShowPassword((visible) => !visible)} className="absolute inset-y-0 right-4 text-slate-400 hover:text-sky-500" aria-label={showPassword ? "Hide password" : "Show password"}>
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">
                Confirm Password <span className="text-rose-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter your password"
                  required
                  className={`w-full rounded-2xl border px-4 py-3 pr-12 text-sm font-semibold transition focus:border-sky-500 focus:outline-none focus:ring-4 focus:ring-sky-500/10 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300 bg-slate-50 text-slate-900"}`}
                />
                <button type="button" onClick={() => setShowConfirmPassword((visible) => !visible)} className="absolute inset-y-0 right-4 text-slate-400 hover:text-sky-500" aria-label={showConfirmPassword ? "Hide confirmation password" : "Show confirmation password"}>
                  {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>

            <label className="flex items-start gap-2.5 text-xs text-slate-500 pt-1 cursor-pointer">
              <input
                type="checkbox"
                checked={agreeTerms}
                onChange={(e) => setAgreeTerms(e.target.checked)}
                className="mt-0.5 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
              />
              <span>
                I agree to the ClassNX Terms of Service, Tuition Data Processing Agreement, and Privacy Policy.
              </span>
            </label>

            <button
              type="submit"
              disabled={submitting}
              className="mt-2 w-full flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-sky-600 to-blue-600 py-3.5 px-4 font-bold text-white shadow-lg shadow-sky-600/25 transition hover:from-sky-700 hover:to-blue-700 active:scale-[0.99] disabled:opacity-60"
            >
              <FaLock /> {submitting ? "Activating..." : "Complete Activation & Sign In"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
